import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  useSubscriptionCheck,
  useStripeCheckoutReturn,
  formatTrialDetail,
  getTrialCheckoutContent,
  type CheckoutPlanType,
  type CheckoutPlanContent,
  type BillingInterval,
} from '@/features/advisor-onboarding';
import { useStripeCheckoutSession } from '@client/hooks/useStripeCheckoutSession';

import { selectStripeSubscribeSessionState } from '@client/model/selectors';
import { resetStripeSubscribeSession } from '@client/model/stripeSubscribeSessionModel';

type ViewState = 'plan-selection' | 'checkout';
type PlanType = 'monthly' | 'annual';
/** Matches useStripeCheckoutSession pendingPlanType (includes 'trial' for legacy/other callers). */
type PendingPlanType = 'trial' | PlanType;

const VALID_INTERVALS: BillingInterval[] = ['monthly', 'annual'];

function isValidInterval(value: string | null): value is BillingInterval {
  return value !== null && VALID_INTERVALS.includes(value as BillingInterval);
}

interface UseStartTrialStepOptions {
  advisorId: string;
}

interface UseStartTrialStepResult {
  viewState: ViewState;
  selectedInterval: BillingInterval;
  setSelectedInterval: (interval: BillingInterval) => void;
  isPending: boolean;
  pendingPlanType: PendingPlanType | null;
  isCheckoutLoading: boolean;
  clientSecret: string | null;
  hasActiveSubscription: boolean;
  hasPendingInvitation: boolean;
  isInCheckoutFlow: boolean;
  isFetching: boolean;
  checkoutContent: CheckoutPlanContent;
  trialDetailFormatted: string | null;
  handleStartTrial: () => void;
  handleBackToPlans: () => void;
}

export function useStartTrialStep({
  advisorId,
}: UseStartTrialStepOptions): UseStartTrialStepResult {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const viewParam = searchParams.get('view');
  const intervalParam = searchParams.get('interval');
  const planParam = searchParams.get('plan');

  const viewState: ViewState = viewParam === 'checkout' ? 'checkout' : 'plan-selection';
  const selectedInterval: BillingInterval = isValidInterval(intervalParam)
    ? intervalParam
    : planParam === 'annual'
      ? 'annual'
      : 'monthly';

  const setSelectedInterval = useCallback(
    (interval: BillingInterval) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('interval', interval);
        return next;
      });
    },
    [setSearchParams],
  );

  const { createCheckoutSession, isPending, pendingPlanType } = useStripeCheckoutSession({
    advisorId,
    skipNavigation: true,
  });

  const { clientSecret, isLoading: isCheckoutLoading } = useSelector(
    selectStripeSubscribeSessionState,
  );

  const { hasActiveSubscription, hasPendingInvitation, isInCheckoutFlow, isFetching } =
    useSubscriptionCheck({ advisorId });

  useStripeCheckoutReturn();

  const returnPath = `/advisors/${advisorId}/onboarding/activate-seat`;

  const handleStartTrial = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('view', 'checkout');
      next.set('plan', 'free');
      next.set('interval', selectedInterval);
      return next;
    });
    createCheckoutSession({ planType: selectedInterval, returnPath });
  }, [createCheckoutSession, returnPath, selectedInterval, setSearchParams]);

  const handleBackToPlans = useCallback(() => {
    dispatch(resetStripeSubscribeSession());
    setSearchParams({}, { replace: true });
  }, [dispatch, setSearchParams]);

  const hasAttemptedRestoreRef = useRef(false);

  useEffect(() => {
    if (viewState === 'plan-selection') {
      hasAttemptedRestoreRef.current = false;
    }
  }, [viewState]);

  // Re-create checkout session once when in checkout view with no session (e.g. after refresh).
  // Do not retry on failure to avoid an endless request/toast loop.
  useEffect(() => {
    if (
      viewState === 'checkout' &&
      !clientSecret &&
      !isCheckoutLoading &&
      !isPending &&
      !hasAttemptedRestoreRef.current
    ) {
      hasAttemptedRestoreRef.current = true;
      createCheckoutSession({ planType: selectedInterval, returnPath });
    }
  }, [
    viewState,
    clientSecret,
    isCheckoutLoading,
    isPending,
    selectedInterval,
    returnPath,
    createCheckoutSession,
  ]);

  // Scroll to top when transitioning to checkout view
  useEffect(() => {
    if (viewState === 'checkout') {
      document.querySelector('body')?.scrollTo(0, 0);
      window.scrollTo(0, 0);
    }
  }, [viewState]);

  const checkoutContent = getTrialCheckoutContent(selectedInterval);
  const trialDetailFormatted = checkoutContent.hasTrial
    ? formatTrialDetail(checkoutContent.trialDetail, checkoutContent.trialDays)
    : null;

  // Combine loading states: show loading if mutation is pending OR Redux says it's loading
  // This prevents the error message from flashing while transitioning to checkout
  const isCheckoutSessionLoading = isPending || isCheckoutLoading;

  return {
    viewState,
    selectedInterval,
    setSelectedInterval,
    isPending,
    pendingPlanType,
    isCheckoutLoading: isCheckoutSessionLoading,
    clientSecret: clientSecret ?? null,
    hasActiveSubscription,
    hasPendingInvitation,
    isInCheckoutFlow,
    isFetching,
    checkoutContent,
    trialDetailFormatted,
    handleStartTrial,
    handleBackToPlans,
  };
}
